import { Combobox } from "@headlessui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useDebounce } from "hooks/use-debounce";
import { useRouter } from "next/navigation";
import { startTransition, useState } from "react";
import { twMerge } from "tailwind-merge";

import { links } from "@/data/links";
import {
  SymbolData,
  useSearchSymbol,
} from "@/features/analysis/search/services/search-symbol";
import { useLang } from "@/hooks/use-lang";
import { formatSymbol } from "@/utils/i18n/format-symbol";

export const SymbolSearch = () => {
  const router = useRouter();
  const { t, lang } = useLang();

  const [query, setQuery] = useState("");
  const deferredQuery = useDebounce(query, 500);
  const { data, isError, isLoading } = useSearchSymbol(deferredQuery);

  return (
    <Combobox
      as="div"
      className="relative max-w-[284px] flex-1"
      value=""
      onChange={(symbol) =>
        router.push(`/${lang}${links.myAnalysisOverview({ symbol })}`)
      }
    >
      <Combobox.Input
        className="w-full rounded-md border-0 bg-white bg-opacity-10 py-1.5 pl-10 pr-3 text-sm leading-6 text-white shadow-sm ring-1 ring-inset ring-gray-300/10 placeholder:text-white/80 focus:ring-1 focus:ring-inset focus:ring-sky-700/10"
        onChange={(event) => {
          startTransition(() => {
            setQuery(event.target.value);
          });
        }}
        displayValue={({ symbol }: SymbolData) => symbol}
        placeholder={t.layout.navbar.searchPlaceholder}
        aria-label="search stocks"
      />
      <Combobox.Button className="absolute inset-y-0 left-0 flex items-center rounded-r-md px-2 focus:outline-none">
        <MagnifyingGlassIcon
          className="h-5 w-5 text-white"
          aria-hidden="true"
        />
      </Combobox.Button>

      {query && (
        <Combobox.Options
          className={twMerge(
            "absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none",
            !isLoading && (isError || (data && data.length === 0)) && "hidden"
          )}
        >
          {isLoading ? (
            <LoadingSymbolSearch />
          ) : (
            data &&
            data.map(({ symbol, name }, i) => (
              <Combobox.Option
                key={`${symbol}-${i}`}
                value={symbol}
                className={({ active }) =>
                  twMerge(
                    "relative cursor-default select-none px-3 py-2 hover:bg-sky-700/5",
                    active && "bg-sky-700/5"
                  )
                }
              >
                <div className="flex items-center gap-3 truncate">
                  <p className="w-14 shrink-0 text-sm font-medium text-zinc-900">
                    {formatSymbol(symbol)}
                  </p>
                  <p className="truncate text-sm text-zinc-500">{name}</p>
                </div>
              </Combobox.Option>
            ))
          )}
        </Combobox.Options>
      )}
    </Combobox>
  );
};

const LoadingSymbolSearch = () => (
  <div className="animate flex animate-pulse flex-col gap-y-5 border-none px-3 py-5">
    <div className="h-3 w-6/12 rounded-lg bg-gray-300" />
    <div className="h-3 w-4/12 rounded-lg bg-gray-300" />
    <div className="h-3 w-6/12 rounded-lg bg-gray-300" />
    <div className="h-3 w-4/12 rounded-lg bg-gray-300" />
  </div>
);
